import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { message } from 'antd'
import { useToken } from 'app/hooks'
import API from 'utils/api'
import { useTeacherStudent } from 'app/gomsacademy/hooks/TeacherStudentList'
import { useGetMyInfo } from 'app/gomsacademy/hooks/GetMyInfo'

type RecipientType = 'parent' | 'student'

const Container = styled.div`
	width: 100%;
`

const Row = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;

	@media (max-width: 900px) {
		grid-template-columns: 1fr;
	}
`

const Card = styled.div`
	border: 1px solid #f6f1f1;
	border-radius: 12px;
	background: #fff;
	padding: 16px;
`

const CardTitle = styled.div`
	font-size: 16px;
	font-weight: 700;
	margin-bottom: 12px;
	color: #111;
`

const Controls = styled.div`
	display: flex;
	gap: 12px;
	flex-wrap: wrap;
	margin-bottom: 12px;
`

const Select = styled.select`
	padding: 8px 12px;
	border: 1px solid #e4e1e1;
	border-radius: 8px;
	font-size: 14px;
	min-width: 160px;
`

const Input = styled.input`
	padding: 8px 12px;
	border: 1px solid #e4e1e1;
	border-radius: 8px;
	font-size: 14px;
	flex: 1;
	min-width: 180px;
`

const List = styled.div`
	border: 1px solid #f0f0f0;
	border-radius: 8px;
	overflow: hidden;
	max-height: 420px;
	overflow-y: auto;
`

const ListRow = styled.label`
	display: flex;
	gap: 10px;
	align-items: center;
	padding: 10px 12px;
	border-bottom: 1px solid #f5f5f5;
	cursor: pointer;
	user-select: none;

	&:hover {
		background: #fafafa;
	}

	&:last-child {
		border-bottom: none;
	}
`

const Name = styled.span`
	font-weight: 600;
	color: #111;
`

const Meta = styled.span`
	color: #666;
	font-size: 12px;
`

const Footer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-top: 12px;
	flex-wrap: wrap;
`

const Button = styled.button`
	padding: 10px 14px;
	border-radius: 10px;
	border: 1px solid #f78b85;
	background: #f78b85;
	color: #fff;
	font-weight: 700;
	cursor: pointer;

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`

const SecondaryButton = styled.button`
	padding: 10px 14px;
	border-radius: 10px;
	border: 1px solid #e4e1e1;
	background: #fff;
	color: #111;
	font-weight: 700;
	cursor: pointer;
`

const ResultTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	border: 1px solid #f6f1f1;
	margin-top: 16px;

	th {
		padding: 10px;
		background-color: #fff5f5;
		border: 1px solid #f6f1f1;
		text-align: left;
		font-weight: 700;
		color: #333;
		font-size: 13px;
	}
	
	td {
		padding: 10px;
		border: 1px solid #f6f1f1;
		color: #111;
		font-size: 13px;
	}
`

const TextArea = styled.textarea`
	padding: 8px 12px;
	border: 1px solid #e4e1e1;
	border-radius: 8px;
	font-size: 14px;
	width: 100%;
	min-height: 120px;
	resize: vertical;
	font-family: inherit;
`

const PreviewBox = styled.div`
	border: 1px solid #f0f0f0;
	border-radius: 10px;
	background: #fafafa;
	padding: 12px;
	white-space: pre-wrap;
	font-size: 13px;
	line-height: 1.5;
	color: #111;
	min-height: 100px;
`

const PreviewMeta = styled.div`
	margin-top: 8px;
	font-size: 12px;
	color: #666;
`

const Badge = styled.span<{ ok: boolean }>`
	padding: 4px 8px;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 700;
	background-color: ${p => (p.ok ? '#e6f7e6' : '#ffe6e6')};
	color: ${p => (p.ok ? '#52c41a' : '#ff4d4f')};
`

export const GroupMessageSend = () => {
	const { data: token } = useToken()
	const { TeacherStudentData: studentList } = useTeacherStudent(3)
	const { myInfo } = useGetMyInfo()

	const [recipientType, setRecipientType] = useState<RecipientType>('parent')

	const [text, setText] = useState('')
	const [title, setTitle] = useState('')

	const [studentSearch, setStudentSearch] = useState('')
	const [selectedIds, setSelectedIds] = useState<number[]>([])

	const [sending, setSending] = useState(false)
	const [sendResult, setSendResult] = useState<any>(null)

	const previewStudent = useMemo(() => {
		if (!selectedIds.length) return undefined
		const firstId = selectedIds[0]
		const list = Array.isArray(studentList) ? studentList : []
		return list.find((s: any) => Number(s?.id) === Number(firstId))
	}, [selectedIds, studentList])

	const previewMessageText = useMemo(() => {
		if (!text) return ''
		const academyName = String(myInfo?.branchName || myInfo?.deptName || '')
		const studentName = String(previewStudent?.name || '')
		
		let out = text
		// Replace placeholders
		out = out.replace(/#\{학원이름\}/g, academyName)
		out = out.replace(/#\{학원명\}/g, academyName)
		out = out.replace(/#\{학생이름\}/g, studentName)
		out = out.replace(/#\{이름\}/g, studentName)
		
		return out
	}, [text, myInfo, previewStudent])

	const previewTitleText = useMemo(() => {
		if (!title) return ''
		const academyName = String(myInfo?.branchName || myInfo?.deptName || '')
		const studentName = String(previewStudent?.name || '')
		
		let out = title
		// Replace placeholders
		out = out.replace(/#\{학원이름\}/g, academyName)
		out = out.replace(/#\{학원명\}/g, academyName)
		out = out.replace(/#\{학생이름\}/g, studentName)
		out = out.replace(/#\{이름\}/g, studentName)
		
		return out
	}, [title, myInfo, previewStudent])

	const filteredStudents = useMemo(() => {
		const list = Array.isArray(studentList) ? studentList : []
		const keyword = studentSearch.trim().toLowerCase()
		if (!keyword) return list
		return list.filter((s: any) => {
			const name = String(s?.name || '').toLowerCase()
			const phone = String(s?.phone || '')
			const phone2 = String(s?.phone2 || '')
			return name.includes(keyword) || phone.includes(keyword) || phone2.includes(keyword)
		})
	}, [studentList, studentSearch])


	const selectedCount = selectedIds.length

	const toggleAllFiltered = () => {
		const ids = filteredStudents.map((s: any) => Number(s.id)).filter((v: any) => !Number.isNaN(v))
		const allSelected = ids.length > 0 && ids.every(id => selectedIds.includes(id))
		setSelectedIds(
			allSelected
				? selectedIds.filter(id => !ids.includes(id))
				: Array.from(new Set([...selectedIds, ...ids]))
		)
	}

	const toggleOne = (id: number) => {
		setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
	}


	const handleSend = async () => {
		if (!token) return
		if (selectedIds.length === 0) {
			message.warning('수신자를 선택해 주세요.')
			return
		}
		if (!text || !text.trim()) {
			message.warning('메시지 내용을 입력해 주세요.')
			return
		}

		const body: any = {
			studentIds: selectedIds,
			text: text.trim(),
			recipientType,
		}

		if (title && title.trim()) {
			body.title = title.trim()
		}

		if (!window.confirm(`선택한 ${selectedIds.length}명에게 SMS를 전송할까요?`)) return

		setSending(true)
		setSendResult(null)
		try {
			const res: any = await API.post('/message/send/group', token + '', body)
			if (res?.code !== 0) {
				message.error(res?.message || '전송에 실패했습니다.')
				setSendResult(res?.result ?? null)
				return
			}

			// API-level success (code === 0), but group-send can still be partially/fully failed.
			const result = res?.result ?? {}
			const results = Array.isArray(result?.results) ? result.results : []
			const successCount = results.filter((r: any) => !!r?.success).length
			const failCount = results.filter((r: any) => !r?.success).length

			if (result?.success === true) {
				message.success('전송 요청이 완료되었습니다.')
				setSendResult(result)
				return
			}

			// Partial fail / fail (result.success === false)
			const baseMsg = result?.message || '전송에 실패했습니다.'
			if (results.length > 0) {
				message.warning(`${baseMsg} (성공 ${successCount}명 / 실패 ${failCount}명)`)
			} else {
				message.error(baseMsg)
			}
			setSendResult(result)
		} catch (e: any) {
			message.error(e?.message || '전송 중 오류가 발생했습니다.')
		} finally {
			setSending(false)
		}
	}

	return (
		<Container>
			<Row>
				<Card>
					<CardTitle>수신자 선택</CardTitle>
					<Controls>
						<Input
							value={studentSearch}
							onChange={e => setStudentSearch(e.target.value)}
							placeholder="이름/전화번호 검색"
						/>
						<SecondaryButton onClick={toggleAllFiltered}>필터 전체 선택/해제</SecondaryButton>
					</Controls>
					<List>
						{filteredStudents.map((s: any) => {
							const id = Number(s.id)
							const checked = selectedIds.includes(id)
							const parentTel = s?.phone2 ? String(s.phone2) : ''
							const studentTel = s?.phone ? String(s.phone) : ''
							return (
								<ListRow key={id}>
									<input type="checkbox" checked={checked} onChange={() => toggleOne(id)} />
									<div>
										<div>
											<Name>{s?.name}</Name>
										</div>
										<Meta>
											학부모: {parentTel || '-'} / 학생: {studentTel || '-'}
										</Meta>
									</div>
								</ListRow>
							)
						})}
					</List>
					<Footer>
						<div>선택: {selectedCount}명</div>
						<SecondaryButton onClick={() => setSelectedIds([])}>선택 초기화</SecondaryButton>
					</Footer>
				</Card>

				<Card>
					<CardTitle>전송 설정</CardTitle>
					<Controls>
						<Select
							value={recipientType}
							onChange={e => setRecipientType(e.target.value as RecipientType)}>
							<option value="parent">학부모(보호자) 번호</option>
							<option value="student">학생 번호</option>
						</Select>
					</Controls>

					<div style={{ marginTop: 16, marginBottom: 8, fontWeight: 700 }}>
						제목 (선택) - 제목을 입력하면 LMS로 발송됩니다
					</div>
					<Input
						value={title}
						onChange={e => setTitle(e.target.value)}
						placeholder="예: 공지사항, 안내사항 등"
					/>

					<div style={{ marginTop: 16, marginBottom: 8, fontWeight: 700 }}>
						메시지 내용 (필수)
						<span style={{ marginLeft: 8, fontWeight: 600, color: '#666', fontSize: 12 }}>
							사용 가능: {'#{학원이름}'}, {'#{학생이름}'}
						</span>
					</div>
					<TextArea
						value={text}
						onChange={e => setText(e.target.value)}
						placeholder="메시지 내용을 입력하세요. #{학원이름}, #{학생이름} 등의 플레이스홀더를 사용할 수 있습니다."
					/>

					<div style={{ marginTop: 16, marginBottom: 8, fontWeight: 700 }}>
						메시지 내용 미리보기
					</div>
					{title && (
						<div style={{ marginBottom: 8 }}>
							<div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>제목:</div>
							<PreviewBox style={{ minHeight: 'auto', padding: '8px' }}>
								{previewTitleText || title}
							</PreviewBox>
						</div>
					)}
					<PreviewBox>
						{!text
							? '메시지 내용을 입력하면 미리보기가 표시됩니다.'
							: previewMessageText || text}
					</PreviewBox>
					<PreviewMeta>
						미리보기 대상:{' '}
						{previewStudent?.name
							? `${previewStudent.name} (선택 ${selectedIds.length}명 중 1명)`
							: `선택 ${selectedIds.length}명`}
						{myInfo?.branchName ? ` / 학원: ${myInfo.branchName}` : ''}
					</PreviewMeta>

					<Footer>
						<div />
						<Button onClick={handleSend} disabled={sending || !text.trim()}>
							{sending ? '전송 중...' : '단체 SMS 전송'}
						</Button>
					</Footer>
				</Card>
			</Row>

			{sendResult?.results && (
				<ResultTable>
					<thead>
						<tr>
							<th>이름</th>
							<th>전화번호</th>
							<th>결과</th>
						</tr>
					</thead>
					<tbody>
						{sendResult.results.map((r: any, idx: number) => (
							<tr key={idx}>
								<td>{r.name}</td>
								<td>{r.phone || '-'}</td>
								<td>
									<Badge ok={!!r.success}>{r.success ? '성공' : '실패'}</Badge>
									{r.reason ? ` (${r.reason})` : ''}
								</td>
							</tr>
						))}
					</tbody>
				</ResultTable>
			)}
		</Container>
	)
}

export default React.memo(GroupMessageSend)
